import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Switch,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { ExternalLink, Phone, Copy, Check, Eye, MousePointer, TrendingUp, Rocket } from 'lucide-react';

export default function Embed() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    embed_enabled: false,
    embed_cta_type: 'booking_url',
    embed_cta_value: '',
    embed_background_color: '#2563eb',
    embed_text_color: '#ffffff',
    embed_button_background_color: '#ffffff',
    embed_button_text_color: '#2563eb',
    embed_availability_window_enabled: false,
    embed_availability_window_days: 7,
    embed_views_count: 0,
    embed_clicks_count: 0,
    embed_click_through_rate: 0,
    embed_last_viewed_at: null,
    embed_last_clicked_at: null
  });
  const [originalSettings, setOriginalSettings] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [accountProvider, setAccountProvider] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [nextOpening, setNextOpening] = useState(null);
  const [ctaValueError, setCtaValueError] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchAccountId();
  }, []);

  useEffect(() => {
    if (businessId) {
      fetchBannerData();
    }
  }, [businessId]);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/embed_settings');
      const data = await response.json();
      setSettings(data);
      setOriginalSettings(data);
    } catch (error) {
      console.error('Failed to fetch embed settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load embed settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const hasUnsavedChanges = () => {
    if (!originalSettings) return false;
    return (
      settings.embed_enabled !== originalSettings.embed_enabled ||
      settings.embed_cta_type !== originalSettings.embed_cta_type ||
      settings.embed_cta_value !== originalSettings.embed_cta_value ||
      settings.embed_background_color !== originalSettings.embed_background_color ||
      settings.embed_text_color !== originalSettings.embed_text_color ||
      settings.embed_button_background_color !== originalSettings.embed_button_background_color ||
      settings.embed_button_text_color !== originalSettings.embed_button_text_color ||
      settings.embed_availability_window_enabled !== originalSettings.embed_availability_window_enabled ||
      settings.embed_availability_window_days !== originalSettings.embed_availability_window_days
    );
  };

  const fetchAccountId = async () => {
    try {
      const response = await fetch('/api/current_user');
      const data = await response.json();
      const user = data.user;
      const externalAccountProvider = user.business_external_account_provider || user.connected_provider;
      setAccountId(user.business_external_account_id);
      setAccountProvider(externalAccountProvider);
      setBusinessId(user.business_id);
    } catch (error) {
      console.error('Failed to fetch account ID:', error);
    }
  };

  const fetchBannerData = async () => {
    try {
      const response = await fetch('/api/schedule_openings?page=1&sort=earliest&future=true');
      const data = await response.json();
      setNextOpening(data.schedule_openings?.[0] || null);
    } catch (error) {
      console.error('Failed to fetch banner data:', error);
    }
  };

  const previewNextOpening = (() => {
    if (!nextOpening) return null;
    if (!settings.embed_enabled) return null;
    if (!settings.embed_availability_window_enabled) return nextOpening;

    const startTime = nextOpening.start_time ? new Date(nextOpening.start_time) : null;
    if (!startTime) return null;

    const windowEnd = new Date();
    windowEnd.setDate(windowEnd.getDate() + (settings.embed_availability_window_days || 7));

    return startTime <= windowEnd ? nextOpening : null;
  })();

  const formatDateTime = (isoString) => {
    if (!isoString) return null;

    const date = new Date(isoString);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Reset hours for date comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());

    const timeString = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    if (dateOnly.getTime() === todayOnly.getTime()) {
      return `Today at ${timeString}`;
    } else if (dateOnly.getTime() === tomorrowOnly.getTime()) {
      return `Tomorrow at ${timeString}`;
    } else {
      const dateString = date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
      return `${dateString} at ${timeString}`;
    }
  };

  const validateCtaValue = () => {
    if (!settings.embed_cta_value) {
      setCtaValueError('This field is required when embed is enabled');
      return false;
    }

    if (settings.embed_cta_type === 'booking_url') {
      const urlPattern = /^https?:\/\/.+\..+/i;
      if (!urlPattern.test(settings.embed_cta_value)) {
        setCtaValueError('Must be a valid URL starting with http:// or https://');
        return false;
      }
    } else if (settings.embed_cta_type === 'phone_number') {
      const phonePattern = /^[\d\s\-\+\(\)]+$/;
      if (!phonePattern.test(settings.embed_cta_value)) {
        setCtaValueError('Must be a valid phone number (digits, spaces, +, -, (, ) only)');
        return false;
      }

      const digitCount = (settings.embed_cta_value.match(/\d/g) || []).length;
      if (digitCount < 10) {
        setCtaValueError('Must contain at least 10 digits');
        return false;
      }
    }

    setCtaValueError(null);
    return true;
  };

  const handleSave = async () => {
    // Validate if embed is enabled
    if (settings.embed_enabled && !validateCtaValue()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);

    try {
      const response = await fetch('/api/embed_settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          embed_settings: {
            embed_enabled: settings.embed_enabled,
            embed_cta_type: settings.embed_cta_type,
            embed_cta_value: settings.embed_cta_value,
            embed_background_color: settings.embed_background_color,
            embed_text_color: settings.embed_text_color,
            embed_button_background_color: settings.embed_button_background_color,
            embed_button_text_color: settings.embed_button_text_color,
            embed_availability_window_enabled: settings.embed_availability_window_enabled,
            embed_availability_window_days: settings.embed_availability_window_days
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Embed settings saved successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchSettings();
      } else {
        toast({
          title: 'Error',
          description: data.errors?.join(', ') || 'Failed to save settings',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to save embed settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const copyEmbedCode = () => {
    if (!accountId) return;

    const providerParam = accountProvider ? `&provider=${encodeURIComponent(accountProvider)}` : '';
    const embedCode = `<script src="${window.location.origin}/embed.js?external_account_id=${accountId}${providerParam}"></script>`;
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast({
      title: 'Copied!',
      description: 'Embed code copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const providerParam = accountProvider ? `&provider=${encodeURIComponent(accountProvider)}` : '';
  const embedCode = accountId ? `<script src="${window.location.origin}/embed.js?external_account_id=${accountId}${providerParam}"></script>` : '';

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box maxW="1800px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Flex align="center" mb={2}>
            <Rocket size={32} color="#3182ce" style={{ marginRight: '12px' }} />
            <Heading size="xl">Fast Book Banner</Heading>
          </Flex>
          <Text color="gray.600">Show real-time availability on your website to increase conversions</Text>
        </Box>

        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
          {/* Settings Panel */}
          <GridItem>
            <VStack align="stretch" spacing={6}>
              {/* Settings Card */}
              <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" p={6}>
                <Flex align="center" mb={4}>
                  <Heading size="md">Banner Settings</Heading>
                  {hasUnsavedChanges() && (
                    <Badge ml={3} colorScheme="orange" fontSize="xs">
                      Unsaved Changes
                    </Badge>
                  )}
                </Flex>

                {/* Enable/Disable */}
                <FormControl display="flex" alignItems="center" mb={6}>
                  <FormLabel htmlFor="embed-enabled" mb={0}>Enable Embed</FormLabel>
                  <Switch
                    id="embed-enabled"
                    isChecked={settings.embed_enabled}
                    onChange={(e) => setSettings({ ...settings, embed_enabled: e.target.checked })}
                    colorScheme="blue"
                  />
                </FormControl>

                {/* CTA Type */}
                <FormControl mb={6}>
                  <FormLabel>Call-to-Action Type</FormLabel>
                  <RadioGroup
                    value={settings.embed_cta_type}
                    onChange={(value) => {
                      setSettings({ ...settings, embed_cta_type: value });
                      setCtaValueError(null); // Clear error when type changes
                    }}
                  >
                    <Stack spacing={3}>
                      <Box
                        p={3}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                      >
                        <Radio value="booking_url">
                          <HStack spacing={2}>
                            <ExternalLink size={20} />
                            <Text fontWeight="medium">Booking Page URL</Text>
                          </HStack>
                        </Radio>
                      </Box>
                      <Box
                        p={3}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                      >
                        <Radio value="phone_number">
                          <HStack spacing={2}>
                            <Phone size={20} />
                            <Text fontWeight="medium">Phone Number</Text>
                          </HStack>
                        </Radio>
                      </Box>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {/* CTA Value */}
                <FormControl mb={6} isInvalid={ctaValueError}>
                  <FormLabel>
                    {settings.embed_cta_type === 'booking_url' ? 'Booking URL' : 'Phone Number'}
                  </FormLabel>
                  <Input
                    value={settings.embed_cta_value}
                    onChange={(e) => {
                      setSettings({ ...settings, embed_cta_value: e.target.value });
                      setCtaValueError(null); // Clear error when user types
                    }}
                    placeholder={settings.embed_cta_type === 'booking_url' ? 'https://example.com/book' : '+1 (555) 123-4567'}
                  />
                  {ctaValueError && (
                    <FormHelperText color="red.500" mt={2}>
                      {ctaValueError}
                    </FormHelperText>
                  )}
                  {!ctaValueError && (
                    <FormHelperText>
                      {settings.embed_cta_type === 'booking_url'
                        ? 'Enter the full URL including http:// or https://'
                        : 'Enter a phone number with at least 10 digits'
                      }
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Banner Colors */}
                <FormLabel>Banner Colors</FormLabel>
                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4} mb={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Background Color</FormLabel>
                    <HStack>
                      <Input
                        type="color"
                        value={settings.embed_background_color}
                        onChange={(e) => setSettings({ ...settings, embed_background_color: e.target.value })}
                        w="60px"
                        h="40px"
                        p={1}
                        cursor="pointer"
                      />
                      <Input
                        value={settings.embed_background_color}
                        onChange={(e) => setSettings({ ...settings, embed_background_color: e.target.value })}
                        placeholder="#2563eb"
                        fontFamily="mono"
                        fontSize="sm"
                      />
                    </HStack>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Text Color</FormLabel>
                    <HStack>
                      <Input
                        type="color"
                        value={settings.embed_text_color}
                        onChange={(e) => setSettings({ ...settings, embed_text_color: e.target.value })}
                        w="60px"
                        h="40px"
                        p={1}
                        cursor="pointer"
                      />
                      <Input
                        value={settings.embed_text_color}
                        onChange={(e) => setSettings({ ...settings, embed_text_color: e.target.value })}
                        placeholder="#ffffff"
                        fontFamily="mono"
                        fontSize="sm"
                      />
                    </HStack>
                  </FormControl>
                </Grid>

                {/* Button Colors */}
                <FormLabel>Button Colors</FormLabel>
                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4} mb={6}>
                  <FormControl>
                    <FormLabel fontSize="sm">Background Color</FormLabel>
                    <HStack>
                      <Input
                        type="color"
                        value={settings.embed_button_background_color}
                        onChange={(e) => setSettings({ ...settings, embed_button_background_color: e.target.value })}
                        w="60px"
                        h="40px"
                        p={1}
                        cursor="pointer"
                      />
                      <Input
                        value={settings.embed_button_background_color}
                        onChange={(e) => setSettings({ ...settings, embed_button_background_color: e.target.value })}
                        placeholder="#ffffff"
                        fontFamily="mono"
                        fontSize="sm"
                      />
                    </HStack>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Text Color</FormLabel>
                    <HStack>
                      <Input
                        type="color"
                        value={settings.embed_button_text_color}
                        onChange={(e) => setSettings({ ...settings, embed_button_text_color: e.target.value })}
                        w="60px"
                        h="40px"
                        p={1}
                        cursor="pointer"
                      />
                      <Input
                        value={settings.embed_button_text_color}
                        onChange={(e) => setSettings({ ...settings, embed_button_text_color: e.target.value })}
                        placeholder="#2563eb"
                        fontFamily="mono"
                        fontSize="sm"
                      />
                    </HStack>
                  </FormControl>
                </Grid>

                {/* Availability Window */}
                <FormControl mb={6}>
                  <FormLabel>Availability Window</FormLabel>
                  <Box p={4} border="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
                    <Checkbox
                      aria-label="Toggle availability window"
                      isChecked={settings.embed_availability_window_enabled}
                      onChange={(e) => setSettings({
                        ...settings,
                        embed_availability_window_enabled: e.target.checked
                      })}
                      mb={3}
                    >
                      Only show banner if availability exists within
                    </Checkbox>

                    <HStack>
                      <Input
                        type="number"
                        min={1}
                        max={90}
                        value={settings.embed_availability_window_days}
                        onChange={(e) => setSettings({
                          ...settings,
                          embed_availability_window_days: parseInt(e.target.value) || 7
                        })}
                        isDisabled={!settings.embed_availability_window_enabled}
                        width="100px"
                        bg="white"
                      />
                      <Text color={settings.embed_availability_window_enabled ? "gray.700" : "gray.400"}>
                        days from now
                      </Text>
                    </HStack>
                    <FormHelperText mt={2}>
                      {settings.embed_availability_window_enabled
                        ? `Banner will be hidden if the next opening is more than ${settings.embed_availability_window_days} days away`
                        : 'Banner will show any available opening, regardless of how far in the future'
                      }
                    </FormHelperText>
                  </Box>
                </FormControl>

                <Button
                  colorScheme="blue"
                  width="full"
                  size="lg"
                  onClick={handleSave}
                  isLoading={saving}
                  loadingText="Saving..."
                >
                  Save Settings
                </Button>
              </Box>
            </VStack>
          </GridItem>

          {/* Preview & Installation Panel */}
          <GridItem>
            <VStack align="stretch" spacing={6}>
              {/* Live Preview */}
              <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" p={6}>
                <Heading size="md" mb={4}>Live Preview</Heading>

                {previewNextOpening ? (
                  <Box border="1px" borderColor="gray.300" borderRadius="md" overflow="hidden">
                    <Box
                      bg={settings.embed_background_color}
                      color={settings.embed_text_color}
                      p={4}
                      textAlign="center"
                    >
                      <Text as="span" mr={3}>
                        Last-minute opening: {formatDateTime(previewNextOpening.start_time)}
                      </Text>
                      <Button
                        as="a"
                        href={settings.embed_cta_type === 'booking_url' ? settings.embed_cta_value || '#' : `tel:${settings.embed_cta_value || '(555) 123-4567'}`}
                        target={settings.embed_cta_type === 'booking_url' ? '_blank' : undefined}
                        bg={settings.embed_button_background_color}
                        color={settings.embed_button_text_color}
                        _hover={{ opacity: 0.8 }}
                        size="sm"
                      >
                        {settings.embed_cta_type === 'booking_url' ? 'Book Now!' : `Call ${settings.embed_cta_value || '(555) 123-4567'}!`}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box fontSize="sm">
                      <Text fontWeight="medium">Banner will be hidden</Text>
                      <Text mt={1}>
                        {!settings.embed_enabled
                          ? 'Embed is currently disabled'
                          : settings.embed_availability_window_enabled
                          ? `No availability within ${settings.embed_availability_window_days} days - banner will not display`
                          : 'No schedule openings found'
                        }
                      </Text>
                    </Box>
                  </Alert>
                )}
              </Box>

              {/* Installation */}
              <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" p={6}>
                <Heading size="md" mb={4}>Installation</Heading>

                <FormControl mb={4}>
                  <FormLabel>Embed Code</FormLabel>
                  <Box position="relative">
                    <Box
                      bg="gray.50"
                      border="1px"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={4}
                      overflowX="auto"
                    >
                      <Code
                        display="block"
                        whiteSpace="pre"
                        children={embedCode}
                        fontSize="xs"
                      />
                    </Box>
                    <IconButton
                      icon={copied ? <Check size={16} /> : <Copy size={16} />}
                      onClick={copyEmbedCode}
                      isDisabled={!accountId}
                      position="absolute"
                      top={2}
                      right={2}
                      size="sm"
                      colorScheme={copied ? 'green' : 'gray'}
                      aria-label="Copy to clipboard"
                    />
                  </Box>
                </FormControl>

                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box fontSize="sm">
                    <Text fontWeight="medium" mb={2}>How to install:</Text>
                    <VStack align="start" spacing={1} fontSize="sm">
                      <Text>1. Copy the embed code above</Text>
                      <Text>2. Paste it in your website's HTML, just before the closing &lt;/body&gt; tag</Text>
                      <Text>3. Save and publish your changes</Text>
                      <Text>4. The banner will appear at the top of your page when enabled</Text>
                    </VStack>
                  </Box>
                </Alert>
              </Box>
            </VStack>
          </GridItem>
        </Grid>

        {/* Analytics - Full Width */}
        <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" p={6}>
          <Heading size="md" mb={4}>Analytics</Heading>
          <Grid templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
            <Stat>
              <Flex direction="column" align="center">
                <Eye size={24} color="#3182ce" />
                <StatNumber mt={2} fontSize="3xl">{settings.embed_views_count}</StatNumber>
                <StatLabel>Views</StatLabel>
              </Flex>
            </Stat>
            <Stat>
              <Flex direction="column" align="center">
                <MousePointer size={24} color="#38a169" />
                <StatNumber mt={2} fontSize="3xl">{settings.embed_clicks_count}</StatNumber>
                <StatLabel>Clicks</StatLabel>
              </Flex>
            </Stat>
            <Stat>
              <Flex direction="column" align="center">
                <TrendingUp size={24} color="#805ad5" />
                <StatNumber mt={2} fontSize="3xl">{settings.embed_click_through_rate}%</StatNumber>
                <StatLabel>Click-Through Rate</StatLabel>
              </Flex>
            </Stat>
          </Grid>
        </Box>
      </VStack>
    </Box>
  );
}
